import styled from '@emotion/styled';
import { Button, Drawer, Input, Table } from 'antd';
import { ReactNode, useEffect, useState } from 'react';
import { FaBuilding, FaNetworkWired, FaPlus, FaUniversity } from 'react-icons/fa';
import { FiRefreshCcw, FiSearch } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import { PartenaireEntity } from '../../../../entities/partenaire.entity';
import { ROUTES } from '../../../../routes';
import { PRIMARY, YELLOW } from '../../../../shared/colors';
import { Layout } from '../../../shared/Layout';
import { PartenaireForm } from '../components/PartenaireForm';
import { getCustomAgences, getCustomPartenaires } from '../network';

const Container = styled.div`
  min-height: calc(100vh - 76px);
  background:
    radial-gradient(circle at 8% 0%, rgba(255, 79, 31, 0.08), transparent 24%),
    radial-gradient(circle at 92% 8%, rgba(255, 176, 0, 0.08), transparent 22%),
    var(--mt-bg);
  color: var(--mt-text);
  font-family: Poppins-R;

  .content {
    width: 100%;
    padding: 12px;
    color: var(--mt-text);
  }

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: #fff7e8 !important;
    font-weight: bold;
  }

  .odd-row {
    background-color: var(--mt-row-odd);
    color: var(--mt-text);
  }

  .even-row {
    background-color: var(--mt-row-even);
    color: var(--mt-text);
  }

  .odd-row,
  .even-row {
    &:hover > td {
      background-color: var(--mt-row-hover) !important;
      cursor: pointer;
      color: var(--mt-text);
    }
  }

  .admin-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 18px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.13), transparent 38%),
      ${PRIMARY};
    box-shadow: var(--mt-card-shadow);
  }

  .eyebrow {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .admin-hero h1 {
    margin: 6px 0 0;
    color: #fff7e8;
    font-family: Poppins-B;
    font-size: 30px;
    line-height: 1.1;
  }

  .admin-hero p {
    max-width: 720px;
    margin: 8px 0 0;
    color: rgba(255, 247, 232, 0.72);
    line-height: 1.5;
  }

  .hero-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .primary-action,
  .ghost-action {
    border-radius: 999px;
    font-family: Poppins-B;
  }

  .primary-action {
    border: 0;
    background: linear-gradient(135deg, #ff4f1f, ${YELLOW});
    color: #07141c;
  }

  .ghost-action {
    border-color: rgba(255, 176, 0, 0.28);
    background: rgba(255, 255, 255, 0.06);
    color: #fff7e8;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
  }

  .kpi-card,
  .page-toolbar,
  .table-shell {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
  }

  .kpi-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 78px;
    padding: 13px;
  }

  .kpi-card .label {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .kpi-card .value {
    display: block;
    margin-top: 5px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 22px;
  }

  .kpi-card .icon {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: 1px solid rgba(255, 176, 0, 0.24);
    border-radius: 8px;
    color: var(--mt-warning);
    background: var(--mt-surface);
  }

  .page-toolbar {
    display: grid;
    grid-template-columns: minmax(260px, 360px) 1fr auto;
    align-items: center;
    gap: 12px;
    margin: 12px 0;
    padding: 12px;
  }

  .search-box {
    position: relative;
  }

  .search-box svg {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 11px;
    color: var(--mt-muted);
    transform: translateY(-50%);
  }

  .search-box .ant-input {
    height: 38px;
    border-color: var(--mt-border);
    border-radius: 999px;
    padding-left: 34px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .table-shell {
    overflow: hidden;
    padding: 12px;
  }

  .ant-table {
    background: transparent;
    color: var(--mt-text);
  }

  .ant-table-cell {
    border-color: var(--mt-border) !important;
  }

  @media (max-width: 760px) {
    .content {
      padding: 18px 12px;
    }

    .admin-hero,
    .page-toolbar {
      grid-template-columns: 1fr;
      flex-direction: column;
      align-items: flex-start;
    }

    .kpi-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export const PartnerPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [partners, setPartners] = useState<PartenaireEntity[]>([]);
  const [filterPartners, setFilterPartners] = useState<PartenaireEntity[]>([]);
  const history = useHistory();
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;
  const totalAgences = filterPartners.reduce(
    (total, partner) => total + (partner.agences?.length || 0),
    0,
  );

  useEffect(() => {
    getCustomPartenaires(connectedUser.id).then(({ data: partners }) => {
      getCustomAgences(connectedUser.id).then(({ data: agences }) => {
        partners.map((item: PartenaireEntity) => {
          item.agences = [];
          for (let age of agences) {
            if (item.url === age.partenaire) {
              item.agences.push(age);
            }
          }
          return item;
        });
        setPartners(partners);
        setFilterPartners(partners);
        setIsLoading(false);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout>
      <Container>
        <div className='content'>
          <section className='admin-hero'>
            <div>
              <span className='eyebrow'>Administration</span>
              <h1>Partenaires</h1>
              <p>
                Pilotez les sociétés connectées à la plateforme, leurs agences
                et leur couverture opérationnelle.
              </p>
            </div>
            <div className='hero-actions'>
              <Button className='ghost-action' icon={<FiRefreshCcw />} onClick={() => window.location.reload()}>
                Rafraîchir
              </Button>
              <Button
                icon={<FaPlus />}
                className='primary-action'
                onClick={() => setShowDrawer(true)}
              >
                Nouveau partenaire
              </Button>
            </div>
          </section>

          <section className='kpi-grid'>
            <KpiCard label='Partenaires' value={filterPartners.length || '--'} icon={<FaBuilding />} />
            <KpiCard label='Agences rattachées' value={totalAgences || '--'} icon={<FaNetworkWired />} />
            <KpiCard label='Société génératrice' value={connectedUser?.manager?.nom || 'MoranjETrade'} icon={<FaUniversity />} />
          </section>

          <div className='page-toolbar'>
            <div className='search-box'>
              <FiSearch />
              <Input
                placeholder='Rechercher...'
                onChange={(e) => {
                  setFilterPartners(
                    partners.filter(
                      (age) =>
                        age.nom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        age.localisation
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()),
                    ),
                  );
                }}
              />
            </div>
            <span />

            <Button className='ghost-action' icon={<FiRefreshCcw />} onClick={() => window.location.reload()}>
              Actualiser
            </Button>
          </div>

          <div className='table-shell'>
            <Table
              columns={COLUMNS}
              dataSource={filterPartners}
              rowClassName={(row, index) =>
                index % 2 === 0 ? 'even-row' : 'odd-row'
              }
              rowKey='id'
              size='small'
              loading={isLoading}
              style={{ zoom: 0.9 }}
              scroll={{ x: 760 }}
              pagination={false}
              onRow={(row, rowIndex) => {
                return {
                  onClick: (event) => {
                    history.push(ROUTES.PANEL.ADMIN.AGENCE, {
                      partner: row,
                    });
                  }, // click row
                  onDoubleClick: (event) => {}, // double click row
                  onContextMenu: (event) => {}, // right button click row
                  onMouseEnter: (event) => {}, // mouse enter row
                  onMouseLeave: (event) => {}, // mouse leave row
                };
              }}
            />
          </div>
        </div>
        <Drawer
          title='Ajouter un partenaire'
          placement='right'
          width='min(100vw, 520px)'
          onClose={() => setShowDrawer(false)}
          open={showDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <PartenaireForm
            setShowDrawer={setShowDrawer}
            getNewPartenaire={(part) => {
              setPartners([...partners, part]);
              setFilterPartners([...filterPartners, part]);
            }}
          />
        </Drawer>
      </Container>
    </Layout>
  );
};

const KpiCard = ({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) => (
  <div className='kpi-card'>
    <div>
      <span className='label'>{label}</span>
      <strong className='value'>{value}</strong>
    </div>
    <span className='icon'>{icon}</span>
  </div>
);

const COLUMNS = [
  {
    title: 'Code référent',
    dataIndex: 'code_referent',
    flex: 1,
  },
  {
    title: 'Partenaire',
    dataIndex: 'nom',
    flex: 1,
  },
  {
    title: 'Localisation',
    dataIndex: 'localisation',
    flex: 1,
  },
  {
    title: 'Type',
    dataIndex: 'type',
    flex: 1,
  },
  {
    title: "Nombre d'agence",
    render: (cell: any, row: any) => `${row.agences?.length || 0}`,
    flex: 1,
  },
];
